import type { ContentHierarchyNode, ContentHierarchyResponse } from '@/api/admin'
import type { QuizGroupAssociation } from '@/types/quizSet'

export interface AiqInspectionGroup {
  associationId: number
  lessonId: number
  name: string
  sequenceOrder: number
  quizIds: string[]
}

export interface AiqInspectionLesson {
  id: number
  title: string
  sequenceOrder: number
  groups: AiqInspectionGroup[]
}

export interface AiqModuleInspectionModel {
  moduleId: number
  moduleTitle: string
  lessons: AiqInspectionLesson[]
  summary: {
    lessonCount: number
    groupCount: number
    quizCount: number
  }
}

export function parseInspectionId(value: string | null): number | null {
  if (!value) return null
  const id = Number(value)
  return Number.isSafeInteger(id) && id > 0 ? id : null
}

export function findHierarchyNode(
  root: ContentHierarchyNode | ContentHierarchyResponse | undefined,
  id: number,
  type: string,
): ContentHierarchyNode | null {
  if (!root) return null
  if (root.id === id && root.type === type && 'sequenceOrder' in root) return root

  for (const child of root.children) {
    const match = findHierarchyNode(child, id, type)
    if (match) return match
  }

  return null
}

export function buildAiqModuleInspection(
  module: ContentHierarchyNode,
  associations: QuizGroupAssociation[],
): AiqModuleInspectionModel {
  const associationsByLesson = new Map<number, QuizGroupAssociation[]>()

  for (const association of associations) {
    const lessonAssociations = associationsByLesson.get(association.contentId) ?? []
    lessonAssociations.push(association)
    associationsByLesson.set(association.contentId, lessonAssociations)
  }

  const lessons = [...module.children]
    .filter((node) => node.type === 'lesson')
    .sort((a, b) => a.sequenceOrder - b.sequenceOrder)
    .map((node) => ({
      id: node.id,
      title: node.title,
      sequenceOrder: node.sequenceOrder,
      groups: [...(associationsByLesson.get(node.id) ?? [])]
        .sort((a, b) => a.sequenceOrder - b.sequenceOrder || a.id - b.id)
        .map((association) => ({
          associationId: association.id,
          lessonId: node.id,
          name: association.group.name,
          sequenceOrder: association.sequenceOrder,
          quizIds: [...association.group.items]
            .sort((a, b) => a.sequenceOrder - b.sequenceOrder)
            .map((item) => item.quiz.quizId),
        })),
    }))

  const groups = lessons.flatMap((lesson) => lesson.groups)

  return {
    moduleId: module.id,
    moduleTitle: module.title,
    lessons,
    summary: {
      lessonCount: lessons.length,
      groupCount: groups.length,
      quizCount: groups.reduce((total, group) => total + group.quizIds.length, 0),
    },
  }
}
